/**
 * IEIXOP-6695 Dynamic Form UTM — isolated from Contact Us / RFQ.
 *
 * Reads landing UTMs from getInitialSiteLoadQuery() (read-only; never clears that store).
 * Keeps a per-formId snapshot in a separate localStorage key so one Dynamic Form
 * submit only clears that form's UTM entry.
 */

import {
    captureInitialSiteLoadQueryIfNeeded,
    getInitialSiteLoadQuery,
} from "@iewccouk/Utilities/initialSiteLoadQuery";

const STORAGE_KEY = "iewccouk.dynamicFormUtmByFormId";

export interface DynamicFormUtmValues {
    utmSource: string;
    utmMedium: string;
    utmCampaign: string;
    capturedAt: number;
}

type StoreShape = Record<string, DynamicFormUtmValues>;

/** True when localStorage is available (client-only). */
function isBrowser() {
    return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

/** Reads the per–form Id UTM snapshot map from localStorage. */
function readStore(): StoreShape {
    if (!isBrowser()) {
        return {};
    }
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw) as StoreShape;
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

/** Persists the per–form Id UTM snapshot map to localStorage. */
function writeStore(store: StoreShape) {
    if (!isBrowser()) {
        return;
    }
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
        // Quota / private mode: ignore
    }
}

/** Flattens a query param value (string or array) into a single string. */
function queryParamToString(raw: string | string[] | undefined): string {
    if (raw === undefined || raw === null) {
        return "";
    }
    if (Array.isArray(raw)) {
        return raw.filter(Boolean).join(",");
    }
    return String(raw);
}

/** True when at least one UTM field has a non-empty value. */
function hasAnyUtm(values: Pick<DynamicFormUtmValues, "utmSource" | "utmMedium" | "utmCampaign">): boolean {
    return !!(values.utmSource || values.utmMedium || values.utmCampaign);
}

/** Reads utm_source / utm_medium / utm_campaign from a query-param map. */
function readUtmFromParams(
    params: Record<string, string | string[]>,
): Pick<DynamicFormUtmValues, "utmSource" | "utmMedium" | "utmCampaign"> {
    return {
        utmSource: queryParamToString(params.utm_source),
        utmMedium: queryParamToString(params.utm_medium),
        utmCampaign: queryParamToString(params.utm_campaign),
    };
}

/** Parses the current document query string (empty during SSR). */
function readCurrentUrlUtm(): Pick<DynamicFormUtmValues, "utmSource" | "utmMedium" | "utmCampaign"> {
    if (!isBrowser()) {
        return { utmSource: "", utmMedium: "", utmCampaign: "" };
    }
    const params: Record<string, string | string[]> = {};
    const search = window.location.search || "";
    if (search.startsWith("?")) {
        const parsed = new URLSearchParams(search);
        parsed.forEach((value, key) => {
            const existing = params[key];
            if (typeof existing === "undefined") {
                params[key] = value;
            } else if (Array.isArray(existing)) {
                existing.push(value);
            } else {
                params[key] = [existing, value];
            }
        });
    }
    return readUtmFromParams(params);
}

/**
 * Landing UTMs: prefer the current URL (this form page / campaign click), then the
 * first-load store. First-load alone is often empty in CMS/local because the origin
 * was already visited without utm_* params.
 */
function readLandingUtm(): Pick<DynamicFormUtmValues, "utmSource" | "utmMedium" | "utmCampaign"> {
    const fromCurrent = readCurrentUrlUtm();
    if (hasAnyUtm(fromCurrent)) {
        return fromCurrent;
    }

    captureInitialSiteLoadQueryIfNeeded();
    const landing = getInitialSiteLoadQuery();
    return readUtmFromParams(landing?.queryParams ?? {});
}

/**
 * Ensures this formId has a UTM snapshot.
 * Current-URL UTMs refresh the snapshot so a campaign link is not blocked by a
 * stale empty first-load capture. Does not modify Contact Us / RFQ landing storage.
 */
export function ensureDynamicFormUtmSnapshot(formId: string): DynamicFormUtmValues | null {
    const key = formId?.trim();
    if (!key || !isBrowser()) {
        return null;
    }

    const store = readStore();
    const fromCurrent = readCurrentUrlUtm();
    const fromLanding = readLandingUtm();
    const preferred = hasAnyUtm(fromCurrent) ? fromCurrent : fromLanding;

    if (store[key]) {
        const existing = store[key];
        if (hasAnyUtm(fromCurrent) || (!hasAnyUtm(existing) && hasAnyUtm(preferred))) {
            const updated: DynamicFormUtmValues = {
                ...preferred,
                capturedAt: existing.capturedAt,
            };
            store[key] = updated;
            writeStore(store);
            return updated;
        }
        return existing;
    }

    const entry: DynamicFormUtmValues = {
        ...preferred,
        capturedAt: Date.now(),
    };
    store[key] = entry;
    writeStore(store);
    return entry;
}

/** Returns this form's snapshot, creating one from landing query if missing. */
export function getDynamicFormUtm(formId: string): DynamicFormUtmValues | null {
    return ensureDynamicFormUtmSnapshot(formId);
}

/**
 * Clears UTM for this Dynamic Form Id only.
 * Does not call clearInitialSiteLoadQuery() — Contact Us / RFQ stay untouched.
 */
export function clearDynamicFormUtm(formId: string) {
    const key = formId?.trim();
    if (!key || !isBrowser()) {
        return;
    }
    const store = readStore();
    if (!(key in store)) {
        return;
    }

    const nextStore: StoreShape = {};
    Object.entries(store).forEach(([storeKey, value]) => {
        if (storeKey !== key) {
            nextStore[storeKey] = value;
        }
    });
    writeStore(nextStore);
}

/** @internal */
export const dynamicFormUtmStorageKey = STORAGE_KEY;
